import { useRef, useState } from 'react';
import { Loader2, ImagePlus, X, Link as LinkIcon, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import { uploadSocialMedia } from '@/lib/actions';
import { cn } from '@/lib/utils';

/**
 * Selector de medios reutilizable para publicaciones e historias.
 * Permite subir fotos desde archivo (a Storage) o pegar un enlace (Imgur, etc.).
 * Renderiza inputs ocultos compatibles con el FormData existente:
 *  - modo múltiple (publicaciones): `mediaUrl-1`...`mediaUrl-N`
 *  - modo único (historias): `mediaUrl`
 */
export function MediaUploader({
  single = false,
  max = 10,
  initialUrls = [],
  label = 'Fotos',
  required = false,
}: {
  single?: boolean;
  max?: number;
  initialUrls?: string[];
  label?: string;
  required?: boolean;
}) {
  const { toast } = useToast();
  const limit = single ? 1 : max;
  const [urls, setUrls] = useState<string[]>(initialUrls.filter(Boolean).slice(0, limit));
  const [urlInput, setUrlInput] = useState('');
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const add = (u: string) => {
    const v = u.trim();
    if (!v) return;
    setUrls((prev) => (single ? [v] : [...prev, v].slice(0, limit)));
  };
  const remove = (i: number) => setUrls((prev) => prev.filter((_, idx) => idx !== i));

  const handleFiles = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    const slots = limit - urls.length;
    const chosen = Array.from(files).slice(0, single ? 1 : slots);
    const added: string[] = [];
    for (const f of chosen) {
      const res = await uploadSocialMedia(f);
      if (res.success && res.url) added.push(res.url);
      else toast({ title: 'Error al subir', description: res.message, variant: 'destructive' });
    }
    setUrls((prev) => (single ? added.slice(0, 1) : [...prev, ...added].slice(0, limit)));
    setUploading(false);
    if (fileRef.current) fileRef.current.value = '';
  };

  const canAddMore = urls.length < limit;

  return (
    <div className="space-y-2">
      <Label className="text-sm">{label} {!single && <span className="text-xs text-muted-foreground">(hasta {max})</span>}</Label>

      {/* Inputs ocultos para el FormData */}
      {single
        ? <input type="hidden" name="mediaUrl" value={urls[0] || ''} required={required} />
        : urls.map((u, i) => <input key={i} type="hidden" name={`mediaUrl-${i + 1}`} value={u} />)}

      {/* Previsualizaciones */}
      {urls.length > 0 && (
        <div className={cn('grid gap-2', single ? 'grid-cols-1' : 'grid-cols-3 sm:grid-cols-4')}>
          {urls.map((u, i) => (
            <div key={i} className={cn('group relative overflow-hidden rounded-lg border border-white/10', single ? 'aspect-video' : 'aspect-square')}>
              <img src={u} alt="" className="h-full w-full object-cover" />
              <button type="button" onClick={() => remove(i)} className="absolute right-1 top-1 flex h-6 w-6 items-center justify-center rounded-full bg-black/70 text-white opacity-0 transition-opacity group-hover:opacity-100">
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}

      {canAddMore && (
        <div className="space-y-2">
          <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading}
            className="flex w-full items-center justify-center gap-2 rounded-lg border-2 border-dashed border-white/15 py-3 text-sm text-muted-foreground transition-colors hover:border-primary/50 hover:text-primary">
            {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImagePlus className="h-4 w-4" />}
            {uploading ? 'Subiendo...' : single ? 'Subir foto desde archivo' : 'Subir fotos desde archivo'}
          </button>
          <input ref={fileRef} type="file" accept="image/*" multiple={!single} className="hidden" onChange={(e) => handleFiles(e.target.files)} />

          <div className="flex items-center gap-2">
            <div className="relative flex-1">
              <LinkIcon className="absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
              <Input value={urlInput} onChange={(e) => setUrlInput(e.target.value)} placeholder="...o pega un enlace (Imgur)" className="h-9 pl-8 text-sm"
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); add(urlInput); setUrlInput(''); } }} />
            </div>
            <Button type="button" variant="secondary" size="sm" onClick={() => { add(urlInput); setUrlInput(''); }} disabled={!urlInput.trim()}><Plus className="h-4 w-4" /></Button>
          </div>
        </div>
      )}
    </div>
  );
}
